import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  Trophy,
  RotateCw,
  AlertTriangle,
  Lock,
  Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSubmitCourseLessonQuizMutation } from '../../api/apiSlice';
import TheoryRenderer from './TheoryRenderer';

// InlineQuiz
// ──────────
// Мини-квиз внутри урока. Логика:
//   • если ранее уже passed=true → просто показать «✓ сдан» и result-summary,
//     но НЕ блокировать повторное прохождение;
//   • иначе показать форму ответов;
//   • после submit:
//      - passed → toast «открыто», cache обновится (см. submitCourseLessonQuiz
//        в apiSlice — он invalidates CourseContent);
//      - не passed → можно попробовать ещё раз; правильные индексы НЕ показываем.
const InlineQuiz = ({ courseId, lesson }) => {
  const passed = !!lesson?.attempt?.passed;
  const passingScore = Number(lesson?.passingScore) || 60;
  const [answers, setAnswers] = useState({}); // questionId → optionIndex
  const [submit, { isLoading }] = useSubmitCourseLessonQuizMutation();
  const [lastResult, setLastResult] = useState(null); // {passed, percentage, breakdown[]}

  // Сбрасываем локальный стейт при смене урока (а не при ре-рендере того же).
  useEffect(() => {
    setAnswers({});
    setLastResult(null);
  }, [lesson?._id]);

  if (!Array.isArray(lesson?.questions) || lesson.questions.length === 0) {
    return (
      <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Квиз пока без вопросов — обратитесь к автору курса.
        </p>
      </div>
    );
  }

  const totalQ = lesson.questions.length;
  const allAnswered = lesson.questions.every((q) => answers[q._id] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered) return;
    try {
      const payload = lesson.questions.map((q) => ({
        questionId: q._id,
        selectedOptionIndex: answers[q._id],
      }));
      const r = await submit({
        courseId,
        lessonId: lesson._id,
        answers: payload,
      }).unwrap();
      setLastResult(r);
      if (r.passed) {
        toast.success(`Сдано: ${r.percentage}%. Следующие уроки открыты.`);
      } else {
        toast.error(`Не сдано: ${r.percentage}% (нужно ≥ ${passingScore}%)`);
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Не удалось отправить ответы');
    }
  };

  // Если квиз уже сдан и нет свежей попытки — показываем «успех» с возможностью пройти ещё раз.
  if (passed && !lastResult) {
    return (
      <div className="rounded-2xl border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-950/20 p-5">
        <div className="flex items-center gap-3 mb-2">
          <Trophy className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          <div>
            <p className="text-base font-semibold text-emerald-900 dark:text-emerald-100">
              Квиз сдан · {lesson.attempt.percentage}%
            </p>
            <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80">
              Попыток: {lesson.attempt.attempts || 1}. Следующие уроки открыты.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAnswers({})}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-emerald-800 dark:text-emerald-200 hover:underline"
        >
          <RotateCw className="w-3.5 h-3.5" /> Пройти ещё раз
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
        <p className="text-sm text-indigo-900 dark:text-indigo-100">
          Чтобы открыть следующие уроки, наберите минимум{' '}
          <b>{passingScore}%</b> правильных ответов.
        </p>
      </div>

      {lesson.questions.map((q, i) => {
        const sel = answers[q._id];
        const review = lastResult?.breakdown?.find(
          (b) => String(b.questionId) === String(q._id)
        );
        return (
          <div
            key={q._id}
            className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 sm:p-5"
          >
            <div className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-3 break-words flex gap-2">
              <span className="shrink-0">{i + 1}.</span>
              {/* Текст вопроса рендерим через TheoryRenderer — поддерживает
                  markdown (**жирный**, _курсив_, заголовки) и KaTeX-формулы
                  ($x^2$, $$\int...$$). Тот же контракт что у теории-урока. */}
              <div className="min-w-0 flex-1">
                <TheoryRenderer content={q.text || ''} />
              </div>
            </div>
            <div className="space-y-2">
              {q.options.map((o, k) => {
                const active = sel === k;
                const showResultMark = review && lastResult?.passed;
                const isCorrect = showResultMark && k === q.correctOptionIndex;
                const isWrongPicked =
                  showResultMark && k === review.selectedOptionIndex && !review.isCorrect;
                return (
                  <button
                    key={o._id}
                    type="button"
                    onClick={() => setAnswers((a) => ({ ...a, [q._id]: k }))}
                    disabled={lastResult?.passed}
                    className={`w-full text-left rounded-xl border px-4 py-2.5 text-sm sm:text-base transition break-words disabled:opacity-80 ${
                      isCorrect
                        ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-100'
                        : isWrongPicked
                        ? 'border-red-400 bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-100 line-through'
                        : active
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-900 dark:text-indigo-100'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {(isCorrect || isWrongPicked) && (
                        <span className="shrink-0">{isCorrect ? '✓' : '✗'}</span>
                      )}
                      <div className="min-w-0 flex-1 [&>p]:m-0">
                        <TheoryRenderer content={o.text || ''} />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {/* Объяснение — только после успешной сдачи. Тоже rich-text. */}
            {lastResult?.passed && q.explanation && (
              <div className="mt-3 text-xs text-gray-600 dark:text-gray-400 italic break-words [&>p]:m-0">
                <TheoryRenderer content={q.explanation} />
              </div>
            )}
          </div>
        );
      })}

      {/* Submit / результат */}
      {lastResult ? (
        <div
          className={`rounded-2xl border p-5 ${
            lastResult.passed
              ? 'border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-950/20'
              : 'border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20'
          }`}
        >
          <div className="flex items-center gap-3">
            {lastResult.passed ? (
              <CheckCircle2 className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <XCircle className="w-7 h-7 text-red-600 dark:text-red-400" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-gray-900 dark:text-white">
                {lastResult.passed ? 'Сдано!' : 'Не сдано'} · {lastResult.percentage}%
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                {lastResult.score} из {lastResult.totalQuestions} · нужно ≥ {passingScore}%
              </p>
            </div>
            {!lastResult.passed && (
              <button
                type="button"
                onClick={() => {
                  setLastResult(null);
                  setAnswers({});
                }}
                className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 text-sm font-semibold"
              >
                <RotateCw className="w-4 h-4" /> Попробовать ещё
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!allAnswered || isLoading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-5 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition"
        >
          <Send className="w-4 h-4" />
          {isLoading
            ? 'Проверяем…'
            : allAnswered
            ? 'Отправить ответы'
            : `Ответьте на все вопросы (${
                Object.keys(answers).length
              } / ${totalQ})`}
        </button>
      )}
    </div>
  );
};

export default InlineQuiz;
